import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm, SubmitHandler } from "react-hook-form";
import { useLocation, useHistory } from "react-router-dom";

import Form from "@/components/Form/Form";
import Footer from "@/components/footer/Footer";
import Header from "@/components/header/Header";
import { Button } from "@/components/ui/button";

import { passwordRecoverService } from "@/services/continente-credentials/mobile/PasswordRecoverService";
import { PasswordRecoverValidateIdentifierResponse } from "@/models/continente-credentials/mobile/PasswordRecoverValidateIdentifierResponse";

import { FormError } from "@/models/continente-credentials/common/FormError";
import { PasswordRecoverValidateIdentifier } from "@/models/continente-credentials/mobile/PasswordRecoverValidateIdentifier";
import { tryCatch } from "@/utils/try-catch";
import { useApiResponseHandler } from "@/hooks/useApiResponseHandler";
import useFooterDisplayMode from "@/hooks/useFooterDisplayMode";

type Form = PasswordRecoverValidateIdentifier & FormError;

export default function SuccessMessage() {
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const { displayMode, excludeAssetKey } = useFooterDisplayMode();
  const location = useLocation<PasswordRecoverValidateIdentifierResponse>();
  const identifier =
    location.state != null ? location.state.properties.identifier : "";

  const nextStep = location.state?.nextStep || "";

  const { handleResponse } = useApiResponseHandler();

  const formState = useForm<Form>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  const {
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors },
  } = formState;

  const onSubmit: SubmitHandler<PasswordRecoverValidateIdentifier> = async (
    data
  ) => {
    setLoading(true);
    const { data: response, error } = await tryCatch(passwordRecoverService.validateIdentifier(
      {
        identifier: identifier,
        nextStep: location.state.nextStep,
        email: location.state.properties.email,
        flowType: location.state.properties.flowType,
      },
      nextStep
    ));

    handleResponse(
      response,
      {
        setError
      },
      error
    );

    setLoading(false);
  };

  return (
    <Form
      id="SuccessMessage"
      form={formState}
      autoComplete="off"
      onSubmit={(e) => {
        clearErrors();
        handleSubmit(onSubmit)(e);
      }}
    >
      <div className="justify-center">
        <Header
          title={t("password_recover_success.header_title")}
          subtitle={t("password_recover_success.header_message")}
          showLoading={loading}
        />
      </div>
      <Button
        type="submit"
        aria-label={t("accessibility.password_recover.success_message.button.submit")}
      >
        {t("password_recover_success.button_next_message")}
      </Button>
      {displayMode !== "none" && (
        <div
          role="note"
          aria-label={t(displayMode === "carousel" ? "accessibility.signin.icons_carousel" : "accessibility.signin.icons")}
          className="flex flex-col">
          <Footer displayMode={displayMode} excludeAssetKey={excludeAssetKey} />
        </div>
      )}
    </Form>
  );
}
